import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useMeeting, useUpdateMeeting, useDeleteMeeting } from '../api/meetings'
import { useCreateSpecificationVersion } from '../api/leads'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { GenerateSpecModal } from '../components/meetings/GenerateSpecModal'
import { cn, formatDate } from '../lib/utils'
import type { MeetingStatus } from '@1130-studio/shared'
import { MEETING_STATUSES } from '@1130-studio/shared'

const statusLabels: Record<MeetingStatus, string> = {
  scheduled: 'Запланирована',
  ongoing: 'В процессе',
  completed: 'Завершена',
  transcribed: 'Расшифрована',
  cancelled: 'Отменена',
}

const statusColors: Record<MeetingStatus, string> = {
  scheduled: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  ongoing: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  completed: 'bg-green-500/15 text-green-400 border-green-500/25',
  transcribed: 'bg-violet-500/15 text-violet-400 border-violet-500/25',
  cancelled: 'bg-red-500/15 text-red-400 border-red-500/25',
}

function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function toLocalDatetimeValue(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const offset = d.getTimezoneOffset()
  const local = new Date(d.getTime() - offset * 60000)
  return local.toISOString().slice(0, 16)
}

export function MeetingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: meeting, isLoading, error } = useMeeting(id!)
  const updateMeeting = useUpdateMeeting()
  const deleteMeeting = useDeleteMeeting()
  const createSpecVersion = useCreateSpecificationVersion()

  const [isEditing, setIsEditing] = useState(false)
  const [transcriptDraft, setTranscriptDraft] = useState('')
  const [showTranscript, setShowTranscript] = useState(true)
  const [showGenerateSpec, setShowGenerateSpec] = useState(false)

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduledAt: '',
    durationMinutes: 30,
    meetingUrl: '',
  })

  const initForm = () => {
    if (meeting) {
      setFormData({
        title: meeting.title,
        description: meeting.description || '',
        scheduledAt: toLocalDatetimeValue(meeting.scheduledAt),
        durationMinutes: meeting.durationMinutes,
        meetingUrl: meeting.meetingUrl || '',
      })
    }
  }

  const handleEdit = () => {
    initForm()
    setIsEditing(true)
  }

  const handleSave = async () => {
    await updateMeeting.mutateAsync({
      id: id!,
      data: {
        title: formData.title,
        description: formData.description || null,
        scheduledAt: new Date(formData.scheduledAt).toISOString(),
        durationMinutes: formData.durationMinutes,
        meetingUrl: formData.meetingUrl || null,
      },
    })
    setIsEditing(false)
  }

  const handleStatusChange = async (status: MeetingStatus) => {
    await updateMeeting.mutateAsync({
      id: id!,
      data: { status },
    })
  }

  const handleDelete = async () => {
    if (window.confirm('Удалить встречу? Это действие нельзя отменить.')) {
      await deleteMeeting.mutateAsync(id!)
      navigate('/meetings')
    }
  }

  const handleSaveTranscript = async () => {
    if (!transcriptDraft.trim()) return
    await updateMeeting.mutateAsync({
      id: id!,
      data: { transcript: transcriptDraft.trim() },
    })
    setTranscriptDraft('')
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-secondary">Загрузка...</div>
      </div>
    )
  }

  // Error state
  if (error || !meeting) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-400 text-lg font-medium mb-2">Встреча не найдена</p>
          <Link
            to="/meetings"
            className="text-sm text-text-secondary hover:text-white transition-colors"
          >
            Вернуться к встречам
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="px-8 py-5 border-b border-border-subtle bg-bg-secondary">
        <div className="flex items-center gap-4">
          <Link
            to="/meetings"
            className="w-8 h-8 rounded-md bg-bg-tertiary flex items-center justify-center text-text-tertiary hover:text-text-primary transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Link>

          <div className="flex-1 flex items-center gap-3">
            {isEditing ? (
              <Input
                value={formData.title}
                onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                className="text-xl font-bold"
              />
            ) : (
              <>
                <h1 className="text-2xl font-bold tracking-tight">{meeting.title}</h1>
                <span
                  className={cn(
                    'px-2.5 py-1 rounded-md text-xs font-medium border',
                    statusColors[meeting.status]
                  )}
                >
                  {statusLabels[meeting.status]}
                </span>
              </>
            )}
          </div>

          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="secondary" onClick={() => setIsEditing(false)}>
                  Отмена
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  loading={updateMeeting.isPending}
                >
                  {updateMeeting.isPending ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={handleEdit}>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Редактировать
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Удалить
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto p-8">
        <div className="grid grid-cols-3 gap-6 max-w-6xl">
          {/* Left column: Info + Description */}
          <div className="col-span-2 space-y-6">
            {/* Info Card */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Информация о встрече</h3>

                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Дата и время"
                        type="datetime-local"
                        value={formData.scheduledAt}
                        onChange={(e) => setFormData((p) => ({ ...p, scheduledAt: e.target.value }))}
                      />
                      <Input
                        label="Длительность (мин)"
                        type="number"
                        min={1}
                        value={String(formData.durationMinutes)}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, durationMinutes: parseInt(e.target.value) || 0 }))
                        }
                      />
                    </div>
                    <Input
                      label="Ссылка на встречу"
                      type="url"
                      value={formData.meetingUrl}
                      onChange={(e) => setFormData((p) => ({ ...p, meetingUrl: e.target.value }))}
                      placeholder="https://meet.google.com/..."
                    />
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Описание
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                        className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20 resize-none"
                        rows={4}
                        placeholder="Описание встречи..."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-text-tertiary">Заявка</span>
                        <p className="font-medium">
                          {meeting.lead ? (
                            <Link
                              to={`/leads`}
                              className="text-accent-blue hover:underline"
                            >
                              {meeting.lead.name}
                            </Link>
                          ) : (
                            <span className="text-text-tertiary">Не привязана</span>
                          )}
                        </p>
                      </div>

                      <div>
                        <span className="text-sm text-text-tertiary">Ведущий</span>
                        <p className="font-medium">
                          {meeting.host?.username || <span className="text-text-tertiary">--</span>}
                        </p>
                      </div>

                      <div>
                        <span className="text-sm text-text-tertiary">Дата и время</span>
                        <p className="font-medium">{formatDateTime(meeting.scheduledAt)}</p>
                      </div>

                      <div>
                        <span className="text-sm text-text-tertiary">Длительность</span>
                        <p className="font-medium">{meeting.durationMinutes} мин</p>
                      </div>

                      {meeting.meetingUrl && (
                        <div className="col-span-2">
                          <span className="text-sm text-text-tertiary">Ссылка на встречу</span>
                          <p>
                            <a
                              href={meeting.meetingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent-blue hover:underline break-all"
                            >
                              {meeting.meetingUrl}
                            </a>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Description (read-only, outside edit mode) */}
            {!isEditing && meeting.description && (
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-3">Описание</h3>
                  <p className="text-text-secondary whitespace-pre-wrap">{meeting.description}</p>
                </div>
              </Card>
            )}

            {/* Transcript Section */}
            <Card>
              <div className="p-6">
                <button
                  onClick={() => setShowTranscript((v) => !v)}
                  className="flex items-center gap-2 w-full text-left"
                >
                  <svg
                    className={cn(
                      'w-4 h-4 text-text-tertiary transition-transform',
                      showTranscript && 'rotate-90'
                    )}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                  <h3 className="text-lg font-semibold text-white">Транскрипт</h3>
                </button>

                {showTranscript && (
                  <div className="mt-4 space-y-4">
                    {meeting.transcript ? (
                      <>
                        <div className="bg-bg-tertiary rounded-lg p-4 max-h-96 overflow-auto">
                          <pre className="whitespace-pre-wrap text-sm text-text-secondary font-mono leading-relaxed">
                            {meeting.transcript}
                          </pre>
                        </div>

                        {/* Generate Spec button */}
                        <div className="flex justify-end">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowGenerateSpec(true)}
                          >
                            <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
                            </svg>
                            Сгенерировать ТЗ
                          </Button>
                        </div>

                        {meeting.transcriptSummary && (
                          <div>
                            <h4 className="text-sm font-semibold text-white mb-2">Саммари</h4>
                            <p className="text-sm text-text-secondary whitespace-pre-wrap">
                              {meeting.transcriptSummary}
                            </p>
                          </div>
                        )}

                        {meeting.transcriptActionItems && meeting.transcriptActionItems.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-white mb-2">Action Items</h4>
                            <ul className="space-y-1.5">
                              {meeting.transcriptActionItems.map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-text-secondary">
                                  <input
                                    type="checkbox"
                                    className="mt-0.5 rounded border-border-medium bg-bg-tertiary accent-accent-lime"
                                    readOnly
                                  />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    ) : (
                      <div>
                        <p className="text-text-tertiary text-sm mb-3">
                          Транскрипт ещё не загружен
                        </p>
                        <textarea
                          value={transcriptDraft}
                          onChange={(e) => setTranscriptDraft(e.target.value)}
                          className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20 resize-none font-mono text-sm"
                          rows={6}
                          placeholder="Вставьте транскрипт встречи..."
                        />
                        {transcriptDraft.trim() && (
                          <div className="mt-2 flex justify-end">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={handleSaveTranscript}
                              loading={updateMeeting.isPending}
                            >
                              Сохранить транскрипт
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Right column: Status + Dates */}
          <div className="space-y-6">
            {/* Status */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Статус</h3>
                <div className="space-y-1.5">
                  {MEETING_STATUSES.map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      disabled={updateMeeting.isPending}
                      className={cn(
                        'w-full px-3 py-2 rounded-md text-sm font-medium text-left transition-colors border',
                        meeting.status === status
                          ? statusColors[status]
                          : 'border-transparent text-text-tertiary hover:text-text-primary hover:bg-white/5'
                      )}
                    >
                      {statusLabels[status]}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            {/* Dates */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Даты</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-text-tertiary">Создана</span>
                    <span className="text-sm">{formatDate(meeting.createdAt)}</span>
                  </div>
                  {meeting.startedAt && (
                    <div className="flex justify-between">
                      <span className="text-text-tertiary">Начата</span>
                      <span className="text-sm">{formatDateTime(meeting.startedAt)}</span>
                    </div>
                  )}
                  {meeting.endedAt && (
                    <div className="flex justify-between">
                      <span className="text-text-tertiary">Завершена</span>
                      <span className="text-sm">{formatDateTime(meeting.endedAt)}</span>
                    </div>
                  )}
                  {meeting.transcribedAt && (
                    <div className="flex justify-between">
                      <span className="text-text-tertiary">Расшифрована</span>
                      <span className="text-sm">{formatDate(meeting.transcribedAt)}</span>
                    </div>
                  )}
                  {meeting.actualDuration && (
                    <div className="flex justify-between">
                      <span className="text-text-tertiary">Факт. длит.</span>
                      <span className="text-sm">{meeting.actualDuration} мин</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-text-tertiary">Обновлена</span>
                    <span className="text-sm">{formatDate(meeting.updatedAt)}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Generate Spec Modal */}
      <GenerateSpecModal
        meetingId={id!}
        isOpen={showGenerateSpec}
        onClose={() => setShowGenerateSpec(false)}
        onSave={meeting?.leadId ? (specification) => {
          createSpecVersion.mutate({
            leadId: meeting.leadId!,
            content: specification,
            changeSummary: `Сгенерировано AI из встречи "${meeting.title}"`,
            isAiGenerated: true,
          })
        } : undefined}
      />
    </div>
  )
}
